import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import WeeklyShipService from './WeeklyShipService';
import { formatDate } from '../../utils/dateUtils';

// Dealer parcels parked as project-HOLD (imported but unmatched — blank/unverified/per_pcs).
// These never appear in the round-scoped batch view, so if one physically didn't arrive there
// was no way to flag it. Here you confirm by its dealer box no + the round it SHOULD have
// arrived in, and mark it "not-arrived" (which locks it out of the HOLD-clear list).
const HeldDealerItemsModal = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUuid, setBusyUuid] = useState(null);
  const [zoomUrl, setZoomUrl] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    WeeklyShipService.getHeldAirItems()
      .then((r) => setItems((r.data || []).filter((it) => it.dealerBoxId)))
      .catch(() => toast.error('โหลดรายการพัสดุพักไม่ได้'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markNotArrived = async (item) => {
    setBusyUuid(item.uuid);
    try {
      await WeeklyShipService.markDealerBoxNotArrived(item.uuid);
      setItems((prev) => prev.filter((it) => it.uuid !== item.uuid));
      toast.success(`Dealer #${item.dealerBoxNo}: ทำเครื่องหมาย "ยังไม่มาส่ง" แล้ว`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ทำเครื่องหมายไม่สำเร็จ');
    }
    setBusyUuid(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">พัสดุ Dealer ที่พักไว้ (HOLD)</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-2 text-xs text-gray-400 border-b border-gray-100 bg-gray-50">
          พัสดุ Dealer ที่นำเข้าแล้วแต่ยังไม่ได้จับคู่ — ถ้ากล่องไหนไม่มาส่ง กด "ยังไม่มาส่ง"
          (ยืนยันจากเลข Dealer + รอบที่ควรถึง)
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <p className="text-sm text-gray-400 text-center py-6">กำลังโหลด…</p>}
          {!loading && items.length === 0 && <p className="text-sm text-gray-300 text-center py-6">ไม่มีพัสดุ Dealer ที่พักไว้</p>}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                  <th className="text-left py-2">Tag</th>
                  <th className="text-left py-2">Dealer #</th>
                  <th className="text-left py-2">รอบที่ควรถึง</th>
                  <th className="text-left py-2">รูป</th>
                  <th className="text-left py-2">Tracking JP</th>
                  <th className="text-right py-2">น้ำหนัก</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.uuid} className="border-b border-gray-50">
                    <td className="py-2 font-mono font-semibold text-amber-700">{it.holdTag}</td>
                    <td className="py-2 font-semibold text-gray-700">{it.dealerBoxNo ?? '-'}</td>
                    <td className="py-2 text-gray-600">{it.originShipDate ? formatDate(it.originShipDate) : '-'}</td>
                    <td className="py-2">
                      {it.shipImageUrl && it.shipImageUrl.startsWith('http') ? (
                        <img src={it.shipImageUrl} alt="" onClick={() => setZoomUrl(it.shipImageUrl)} className="w-12 h-12 object-cover rounded border border-gray-200 cursor-zoom-in" />
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-2 font-mono text-gray-600">
                      {it.trackingJp || <span className="text-gray-400 italic">-</span>}
                    </td>
                    <td className="py-2 text-right text-gray-600">{it.weight != null ? `${it.weight} kg` : '-'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => markNotArrived(it)}
                        disabled={busyUuid === it.uuid}
                        className="px-3 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50"
                      >
                        {busyUuid === it.uuid ? '...' : 'ยังไม่มาส่ง'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {zoomUrl && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setZoomUrl(null)}>
          <img src={zoomUrl} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default HeldDealerItemsModal;
